import { calculateDepositCycle } from '../utils/depositCycleUtils';

// Constantes
const TRANSACTIONS_PER_PAGE = 500;
const TRANSACTION_TYPE_SALE = 'venta';
const TRANSACTION_TYPE_REFUND = 'devolucion';
const TRANSACTION_STATUS_APPROVED = 'aprobada';

/**
 * Construye URL para petición de transacciones
 * @private
 */
const buildTransactionURL = (apiUrl, from, to, page, transactionType, status = null) => {
  const params = new URLSearchParams({
    from: from.format(),
    to: to.format(),
    limit: TRANSACTIONS_PER_PAGE,
    page: page,
    transaction_type: transactionType
  });
  
  if (status) {
    params.append('status', status);
  }
  
  return `${apiUrl}/api/v2/transactions?${params.toString()}`;
};

/**
 * Obtiene transacciones de un tipo específico con paginación
 * @private
 */
const fetchTransactionsByType = async (apiUrl, from, to, accessToken, transactionType, status = null) => {
  let allTransactions = [];
  let page = 1;
  let hasMore = true;
  
  while (hasMore) {
    const url = buildTransactionURL(apiUrl, from, to, page, transactionType, status);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'authorization': accessToken }
    });
    
    // Si la API retorna 404, asumimos que no hay transacciones y terminamos
    if (response.status === 404) {
      hasMore = false;
      continue;
    }
    
    if (!response.ok) {
      throw new Error(`Error fetching ${transactionType}: ${response.status}`);
    }
    
    const data = await response.json();
    const transactions = data.data || [];
    
    allTransactions = [...allTransactions, ...transactions];
    hasMore = transactions.length === TRANSACTIONS_PER_PAGE;
    page++;
  }
  
  return allTransactions;
};

/**
 * Obtiene todas las transacciones del ciclo de depósito
 * @param {string} depositDate - Fecha del depósito
 * @param {string} accessToken - Token de autorización
 * @returns {Promise<Array>} Array de transacciones (ventas + devoluciones)
 */
export const fetchCycleTransactions = async (depositDate, accessToken) => {
  const { from, to } = calculateDepositCycle(depositDate);
  const apiUrl = process.env.REACT_APP_API_URL_PROD;
  
  try {
    // Obtener ventas aprobadas y devoluciones en paralelo
    const [sales, refunds] = await Promise.all([
      fetchTransactionsByType(apiUrl, from, to, accessToken, TRANSACTION_TYPE_SALE, TRANSACTION_STATUS_APPROVED),
      fetchTransactionsByType(apiUrl, from, to, accessToken, TRANSACTION_TYPE_REFUND, TRANSACTION_STATUS_APPROVED)
    ]);
    
    return [...sales, ...refunds];
  } catch (error) {
    console.error('Error fetching cycle transactions:', error);
    throw new Error('No se pudieron obtener las transacciones del ciclo');
  }
};

/**
 * Calcula el total del depósito (ventas - devoluciones)
 * @param {Array} transactions - Array de transacciones
 * @returns {number} Total calculado
 */
export const calculateDepositTotal = (transactions) => {
  return transactions.reduce((total, transaction) => {
    const amount = parseFloat(transaction.total || 0);
    const isRefund = transaction.transaction_type === TRANSACTION_TYPE_REFUND;
    return total + (isRefund ? -amount : amount);
  }, 0);
};
